const DB_NAME = "FEMAProjectDB";
const STORE_NAME = "fileHandles";
const DB_VERSION = 1;

interface StoredHandle {
  id: string;
  handle: FileSystemDirectoryHandle;
  timestamp: number;
}

async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
  });
}

export async function saveDirectoryHandle(
  projectPath: string,
  handle: FileSystemDirectoryHandle
): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const data: StoredHandle = {
      id: projectPath,
      handle,
      timestamp: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    console.log("📦 Saved directory handle to IndexedDB:", projectPath);
  } catch (error) {
    console.error("Failed to save directory handle:", error);
    throw error;
  }
}

export async function getDirectoryHandle(
  projectPath: string
): Promise<FileSystemDirectoryHandle | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const data = await new Promise<StoredHandle | undefined>(
      (resolve, reject) => {
        const request = store.get(projectPath);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }
    );

    if (!data) {
      console.log("No directory handle found for:", projectPath);
      return null;
    }

    // Verify we still have permission
    const permission = await data.handle.queryPermission({ mode: "readwrite" });

    if (permission === "granted") {
      console.log("Directory handle retrieved:", projectPath);
      return data.handle;
    } else {
      console.log("Permission denied for stored handle:", projectPath);
      return null;
    }
  } catch (error) {
    console.error("Failed to get directory handle:", error);
    return null;
  }
}

export async function removeDirectoryHandle(
  projectPath: string
): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(projectPath);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    console.log("Removed directory handle:", projectPath);
  } catch (error) {
    console.error("Failed to remove directory handle:", error);
  }
}

export async function clearAllHandles(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    console.log("Cleared all directory handles");
  } catch (error) {
    console.error("Failed to clear handles:", error);
  }
}
